import {
  useDrilldownSchema,
  useFilters,
  useFilterToValues,
} from 'features/drilldown'
import { DrilldownField } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import _ from 'lodash'
import React, { FC, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import {
  GenericPanelData,
  RolodexHistogramAggregationOp,
  RolodexHistogramFilter,
} from 'types'

import { HistoAggregationConfigView } from './configPanel/aggregation/HistoAggregationConfigView'
import { HistoStackingGroup } from './configPanel/histoStackingTypes'
import { useHistogramConfiguration } from './contexts/HistogramConfigurationContext'
import {
  HistogramDataContextProvider,
  useHistogramData,
} from './contexts/HistogramDataContext'
import { HistogramDateBucketingConfiguration } from './histogramDateConfigurationOptions'
import { DrilldownHistogramRenderer } from './HistogramRenderer'
import { PlotlyOrientation } from './plotlyGenerators'

const ConfigViewContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 5px;
  margin-left: 40px;
  justify-content: space-between;
  display: flex;
  flex-wrap: wrap;
  row-gap: 5px;
`

export type SortDirection = 'asc' | 'desc'

export type DrilldownPlotType = 'bar' | 'pie' | 'stacked'

export interface HistogramPanelData extends GenericPanelData {
  aggregationOperation: RolodexHistogramAggregationOp
  groupingColumn: DrilldownField
  aggregationColumn: DrilldownField
  // These are tuples of (histogramCategory, stackingGroupIdx)
  selectedBarIds: ReadonlyArray<[string, number | null]>
  orientation?: PlotlyOrientation
  sortDirection?: SortDirection
  color?: string
  plotType?: DrilldownPlotType
  dateBucketingConfiguration?: HistogramDateBucketingConfiguration
  stackingGroups?: ReadonlyArray<HistoStackingGroup>
  // Defaults to MAX_NUMBER_OF_HISTOGRAM_ROWS if undefined, no truncation if null
  truncateCount?: number | null
}

export const MAX_NUMBER_OF_HISTOGRAM_ROWS = 15

const RawDrilldownHistogram: FC = () => {
  return (
    <HistogramDataContextProvider>
      <DrilldownHistogramInner />
    </HistogramDataContextProvider>
  )
}

const DrilldownHistogramInner: FC = () => {
  const {
    groupingColumn,
    stackingGroups,
    selectedBarIds,
    setSelectedBarIds,
    plotType,
  } = useHistogramConfiguration()
  const {
    histogram,
    bucketedCategoryToOriginalCategories,
    stackFilters,
    hasEverLoaded,
  } = useHistogramData()

  // Usually correspondingEntryIds is 1:1 with the bar ids, but in the case of grouped bars, eg dates, that's not the case.
  // Here we work out which entry ids are actually selected for a given bar id.
  const selectedBarFiltering = useMemo<{
    categories: ReadonlyArray<string>
    filters: ReadonlyArray<RolodexHistogramFilter>
  }>(() => {
    return {
      categories: selectedBarIds
        .map(([category]) => category)
        .flatMap((cat) => bucketedCategoryToOriginalCategories.get(cat) ?? []),
      filters: selectedBarIds.flatMap(([, stackIdx]) => {
        return stackIdx === null || !stackingGroups || !stackFilters[stackIdx]
          ? []
          : stackFilters[stackIdx]
      }),
    }
  }, [
    bucketedCategoryToOriginalCategories,
    selectedBarIds,
    stackFilters,
    stackingGroups,
  ])
  const shouldApplyFilters = useMemo(
    () =>
      selectedBarFiltering.categories.length &&
      selectedBarFiltering.categories.length !==
        _.sum([...bucketedCategoryToOriginalCategories.values()]),
    [
      bucketedCategoryToOriginalCategories,
      selectedBarFiltering.categories.length,
    ]
  )
  const { fields } = useDrilldownSchema()
  const {
    entryType: groupingType,
    key: groupingKey,
    keyOrField: groupByPropertyKey,
  } = fields[groupingColumn]
  useFilterToValues(
    useMemo(
      () =>
        shouldApplyFilters ? new Set(selectedBarFiltering.categories) : null,
      [selectedBarFiltering.categories, shouldApplyFilters]
    ),
    groupingType,
    groupingKey,
    groupByPropertyKey
  )
  useFilters(selectedBarFiltering.filters)

  // If an ID that was previously selected is removed, then drop it from selectedBarIds
  useEffect(() => {
    if (!hasEverLoaded) {
      return
    }
    setSelectedBarIds((prev) => {
      const validSelections = prev.filter(([category, stackIdx]) => {
        const categoryExists = histogram.sortedCategories.includes(category)
        const stackExists =
          stackIdx === null ||
          (stackingGroups && stackIdx <= stackingGroups.length)
        return categoryExists && stackExists
      })

      // Only update state if selections have changed
      return _.isEqual(prev, validSelections) ? prev : validSelections
    })
  }, [
    histogram.sortedCategories,
    stackingGroups,
    setSelectedBarIds,
    hasEverLoaded,
  ])

  const handleBarClick = useCallback(
    (category: string, stackIdx: number | null) => {
      setSelectedBarIds((prev) => {
        const idxOfMatching = prev.findIndex(
          ([prevCat, prevStackIdx]) =>
            prevCat === category && prevStackIdx === stackIdx
        )
        if (idxOfMatching >= 0) {
          // Bar is already selected; remove it
          return [
            ...prev.slice(0, idxOfMatching),
            ...prev.slice(idxOfMatching + 1),
          ]
        } else {
          // Bar is not selected; add it
          const newSelected: [string, number | null] = [category, stackIdx]
          return plotType === 'stacked' ? [newSelected] : [...prev, newSelected]
        }
      })
    },
    [plotType, setSelectedBarIds]
  )

  useEffect(() => {
    if (plotType === 'stacked') {
      setSelectedBarIds((prev) => (prev.length > 1 ? [] : prev))
    }
  }, [plotType, setSelectedBarIds])

  return (
    <>
      <ConfigViewContainer>
        <HistoAggregationConfigView />
      </ConfigViewContainer>
      <DrilldownHistogramRenderer handleBarClick={handleBarClick} />
    </>
  )
}

export const DrilldownHistogram = React.memo(RawDrilldownHistogram)
